import {
  isJobCategory,
  type JobCategory,
} from "@/domains/job-offer/create/constants";
import type { JobSubcategoryKey } from "../types";

export type TaxonomyIndex = {
  categoryLabels: ReadonlyMap<string, string>;
  subcategoryLabels: ReadonlyMap<string, string>;
  categoryOrder: readonly string[];
  /**
   * The subcategory keys of each of the 8 categories, in the order of the endpoint.
   * Keyed by string, as the keys come from the endpoint: a lookup with a key that is
   * not one of the 8 finds nothing.
   */
  childrenByCategory: Readonly<
    Partial<Record<string, readonly JobSubcategoryKey[]>>
  >;
  /** The category each subcategory key of the tree belongs to. */
  parentBySubcategory: Readonly<
    Partial<Record<JobSubcategoryKey, JobCategory>>
  >;
};

// The two records have a null prototype: a key such as `constructor` must find no
// value, not `Object.prototype.constructor`.
export const EMPTY_TAXONOMY_INDEX: TaxonomyIndex = {
  categoryLabels: new Map(),
  subcategoryLabels: new Map(),
  categoryOrder: [],
  childrenByCategory: Object.create(null),
  parentBySubcategory: Object.create(null),
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isKey(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/** The trimmed label, or `""` when it is not a usable string. */
function cleanLabel(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Indexes the backend taxonomy tree. The tree is untrusted: whatever is not
 * usable is skipped, so a malformed response degrades to showing the keys.
 *
 * The grouping is kept for the 8 categories of `jobCategoryEnum` only. A
 * category outside them still gives its labels, but has no children and no
 * parent in the tree. A subcategory key already listed under another category
 * keeps its first parent.
 */
export function buildTaxonomyIndex(tree: unknown): TaxonomyIndex {
  if (!isRecord(tree) || !Array.isArray(tree.categories)) {
    return EMPTY_TAXONOMY_INDEX;
  }

  const categoryLabels = new Map<string, string>();
  const subcategoryLabels = new Map<string, string>();
  const categoryOrder: string[] = [];
  const childrenByCategory: Partial<Record<string, JobSubcategoryKey[]>> =
    Object.create(null);
  const parentBySubcategory: Partial<Record<JobSubcategoryKey, JobCategory>> =
    Object.create(null);

  for (const category of tree.categories) {
    if (!isRecord(category) || !isKey(category.key)) continue;

    // Only a category with a usable label enters the order (and the labels).
    const label = cleanLabel(category.label);
    if (label) {
      categoryLabels.set(category.key, label);
      categoryOrder.push(category.key);
    }

    const parent = isJobCategory(category.key) ? category.key : null;
    if (parent !== null) childrenByCategory[parent] ??= [];
    if (!Array.isArray(category.subcategories)) continue;

    for (const subcategory of category.subcategories) {
      if (!isRecord(subcategory) || !isKey(subcategory.key)) continue;

      const subLabel = cleanLabel(subcategory.label);
      if (subLabel) subcategoryLabels.set(subcategory.key, subLabel);

      if (
        parent === null ||
        parentBySubcategory[subcategory.key] !== undefined
      ) {
        continue;
      }
      parentBySubcategory[subcategory.key] = parent;
      childrenByCategory[parent]?.push(subcategory.key);
    }
  }

  return {
    categoryLabels,
    subcategoryLabels,
    categoryOrder,
    childrenByCategory,
    parentBySubcategory,
  };
}

export function labelOrKey(
  labels: ReadonlyMap<string, string>,
  key: string,
): string {
  return labels.get(key) ?? key;
}

/**
 * Puts first the `keys` the taxonomy `order` knows, in its order, then the
 * other `keys` as they were. A key of `order` that is not in `keys` is never
 * added.
 */
export function orderByTaxonomy<K extends string>(
  keys: readonly K[],
  order: readonly string[],
): K[] {
  const rank = new Map<string, number>();
  for (const [position, key] of order.entries()) {
    if (!rank.has(key)) rank.set(key, position);
  }

  const known = keys.filter((key) => rank.has(key));
  const unknown = keys.filter((key) => !rank.has(key));
  known.sort((a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0));

  return [...known, ...unknown];
}
